import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, 
  CircularProgress, Alert, TablePagination, Dialog, DialogTitle, 
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox
} from '@mui/material';
import {
  Search, Visibility, Add, Edit, Delete, Engineering, 
  Email, Phone, School, Assignment, PersonAdd
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { tecnicosApi } from '../../services/api';
import { Tecnico, TecnicoCreate, TecnicoUpdate, ESPECIALIDADES_TECNICO } from '../../types/expediente';

const TecnicosList: React.FC = () => {
  // Estados principales
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados de búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  
  // Estados del modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTecnico, setEditingTecnico] = useState<Tecnico | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState<TecnicoCreate>({
    codigo: undefined,
    nombre: '',
    apellidos: '',
    dni: '',
    especialidad: '',
    activo: true,
    email: '',
    telefono: '',
    fecha_alta: '',
    titulacion: '',
    colegiado: ''
  });

  const navigate = useNavigate();

  // Cargar técnicos
  useEffect(() => {
    loadTecnicos();
  }, []);

  const loadTecnicos = async () => {
    try {
      setLoading(true);
      const data = await tecnicosApi.getTecnicos(0, 1000); // Cargar todos
      setTecnicos(data);
    } catch (err) {
      setError('Error cargando técnicos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar técnicos
  const filteredTecnicos = tecnicos.filter(tecnico => {
    const matchesSearch = 
      tecnico.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tecnico.apellidos?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tecnico.especialidad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tecnico.colegiado?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tecnico.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesActive = !showOnlyActive || tecnico.activo;
    
    return matchesSearch && matchesActive;
  });

  // Paginación
  const paginatedTecnicos = filteredTecnicos.slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );

  // Manejar formulario
  const handleInputChange = (field: keyof TecnicoCreate, value: any) => {
    setFormData((prev: TecnicoCreate) => ({
      ...prev,
      [field]: value
    }));
  };

  // Abrir modal para crear
  const handleCreate = () => {
    setEditingTecnico(null);
    setFormData({
      codigo: undefined,
      nombre: '',
      apellidos: '',
      dni: '',
      especialidad: '',
      activo: true,
      email: '',
      telefono: '',
      fecha_alta: new Date().toISOString().split('T')[0], // Fecha actual
      titulacion: '',
      colegiado: ''
    });
    setModalOpen(true);
  };

  // Abrir modal para editar
  const handleEdit = (tecnico: Tecnico) => {
    setEditingTecnico(tecnico);
    setFormData({
      codigo: tecnico.codigo,
      nombre: tecnico.nombre,
      apellidos: tecnico.apellidos || '',
      dni: tecnico.dni || '',
      especialidad: tecnico.especialidad || '',
      activo: tecnico.activo,
      email: tecnico.email || '',
      telefono: tecnico.telefono || '',
      fecha_alta: tecnico.fecha_alta ? new Date(tecnico.fecha_alta).toISOString().split('T')[0] : '',
      titulacion: tecnico.titulacion || '',
      colegiado: tecnico.colegiado || ''
    });
    setModalOpen(true);
  };

  // Guardar técnico
  const handleSave = async () => {
    try {
      setModalLoading(true);
      
      // Preparar datos para envío
      const dataToSend = {
        ...formData,
        fecha_alta: formData.fecha_alta ? new Date(formData.fecha_alta).toISOString() : undefined
      };
      
      if (editingTecnico) {
        // Actualizar
        const updateData: TecnicoUpdate = dataToSend;
        await tecnicosApi.updateTecnico(editingTecnico.id, updateData);
      } else {
        // Crear
        await tecnicosApi.createTecnico(dataToSend);
      }
      
      await loadTecnicos();
      setModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error guardando técnico');
    } finally {
      setModalLoading(false);
    }
  };

  // Eliminar técnico
  const handleDelete = async (tecnico: Tecnico) => {
    const action = tecnico.activo ? 'desactivar' : 'eliminar';
    if (!window.confirm(`¿Seguro que quieres ${action} el técnico ${tecnico.nombre} ${tecnico.apellidos}?`)) {
      return;
    }

    try {
      await tecnicosApi.deleteTecnico(tecnico.id);
      await loadTecnicos();
    } catch (err: any) {
      setError(err.response?.data?.detail || `Error ${action}ndo técnico`);
    }
  };

  // Validar formulario
  const isFormValid = () => {
    const dniRegex = /^[0-9]{8}-[A-Z]$/;
    return formData.nombre.trim() !== '' && 
           formData.dni !== undefined &&
           formData.dni.trim() !== '' && 
           dniRegex.test(formData.dni);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          🔧 Técnicos ({filteredTecnicos.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleCreate}
            color="primary"
          >
            Nuevo Técnico
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
          >
            Volver al Dashboard
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Búsqueda y Filtros */}
      <Box mb={3}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Buscar por nombre, apellidos, especialidad, colegiado o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyActive}
                onChange={(e) => setShowOnlyActive(e.target.checked)}
              />
            }
            label="Solo activos"
          />
        </Box>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Técnico</TableCell>
              <TableCell>DNI</TableCell>
              <TableCell>Especialidad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>Colegiado</TableCell>
              <TableCell>Fecha Alta</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTecnicos.map((tecnico) => (
              <TableRow key={tecnico.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Engineering color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {tecnico.nombre} {tecnico.apellidos}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {tecnico.codigo && `Código: ${tecnico.codigo}`}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {tecnico.dni || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <School sx={{ fontSize: 14 }} color="action" />
                    <Typography variant="body2">
                      {tecnico.especialidad || 'Sin especificar'}
                    </Typography>
                  </Box>
                  {tecnico.titulacion && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {tecnico.titulacion}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={tecnico.activo ? 'Activo' : 'Inactivo'} 
                    color={tecnico.activo ? 'success' : 'error'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    {tecnico.email && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email sx={{ fontSize: 14 }} />
                        <Typography variant="body2">{tecnico.email}</Typography>
                      </Box>
                    )}
                    {tecnico.telefono && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Phone sx={{ fontSize: 14 }} />
                        <Typography variant="body2">{tecnico.telefono}</Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Assignment sx={{ fontSize: 14 }} color="action" />
                    <Typography variant="body2" fontFamily="monospace">
                      {tecnico.colegiado || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {tecnico.fecha_alta ? 
                    new Date(tecnico.fecha_alta).toLocaleDateString('es-ES') : 
                    '-'
                  }
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <Button 
                      size="small" 
                      startIcon={<Visibility />} 
                      onClick={() => navigate(`/tecnicos/${tecnico.id}`)}
                    >
                      Ver
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<Edit />} 
                      onClick={() => handleEdit(tecnico)}
                      color="primary"
                    >
                      Editar
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<Delete />} 
                      onClick={() => handleDelete(tecnico)}
                      color="error"
                    >
                      {tecnico.activo ? 'Desactivar' : 'Eliminar'}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {paginatedTecnicos.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={4}>
                    <Engineering sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body1" color="textSecondary">
                      No se encontraron técnicos
                    </Typography>
                    {searchTerm && (
                      <Typography variant="body2" color="textSecondary">
                        Prueba con otros términos de búsqueda
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Paginación */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTecnicos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página:"
        />
      </TableContainer>

      {/* Modal Crear/Editar */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAdd />
            {editingTecnico ? 'Editar Técnico' : 'Nuevo Técnico'}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            {/* Fila 1: Código y Nombres */}
            <Box display="flex" gap={2}>
              <TextField
                label="Código"
                type="number"
                value={formData.codigo || ''}
                onChange={(e) => handleInputChange('codigo', e.target.value ? parseInt(e.target.value) : undefined)}
                sx={{ flex: 1 }}
                helperText="Código único del técnico"
              />
              <TextField
                label="Nombre *"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                required
                sx={{ flex: 1 }}
              />
              <TextField
                label="Apellidos"
                value={formData.apellidos}
                onChange={(e) => handleInputChange('apellidos', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Fila 2: DNI */}
            <Box display="flex" gap={2}>
              <TextField
                label="DNI *"
                value={formData.dni}
                onChange={(e) => handleInputChange('dni', e.target.value.toUpperCase())}
                required
                placeholder="12345678-A"
                helperText="Formato: 12345678-A (8 dígitos, guion y letra)"
                inputProps={{
                  maxLength: 10,
                  pattern: '[0-9]{8}-[A-Z]'
                }}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Fila 3: Especialidad y Titulación */}
            <Box display="flex" gap={2}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Especialidad</InputLabel>
                <Select
                  value={formData.especialidad}
                  label="Especialidad"
                  onChange={(e) => handleInputChange('especialidad', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Sin especificar</em>
                  </MenuItem>
                  {ESPECIALIDADES_TECNICO.map((especialidad: string) => (
                    <MenuItem key={especialidad} value={especialidad}>
                      {especialidad}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Titulación"
                value={formData.titulacion}
                onChange={(e) => handleInputChange('titulacion', e.target.value)}
                sx={{ flex: 1 }}
                helperText="Ej: Arquitecto Técnico, Ingeniero Industrial"
              />
            </Box>
            
            {/* Fila 4: Email y Teléfono */}
            <Box display="flex" gap={2}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Teléfono"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Fila 5: Colegiado y Fecha Alta */}
            <Box display="flex" gap={2}>
              <TextField
                label="Número de Colegiado"
                value={formData.colegiado}
                onChange={(e) => handleInputChange('colegiado', e.target.value)}
                sx={{ flex: 1 }}
                helperText="Ej: COAAT12345, COII67890"
              />
              <TextField
                label="Fecha de Alta"
                type="date"
                value={formData.fecha_alta}
                onChange={(e) => handleInputChange('fecha_alta', e.target.value)}
                sx={{ flex: 1 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            
            {/* Fila 6: Estado Activo */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.activo}
                  onChange={(e) => handleInputChange('activo', e.target.checked)}
                />
              }
              label="Técnico activo"
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setModalOpen(false)}
            disabled={modalLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid() || modalLoading}
            startIcon={modalLoading ? <CircularProgress size={16} /> : undefined}
          >
            {modalLoading ? 'Guardando...' : editingTecnico ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TecnicosList;